// Unified Button Style System for EBIOS RM Application
// This file defines consistent button styles to be used across the entire application

export const buttonStyles = {
    // Base button classes - shared across all buttons
    base: "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",

    // Size variants
    sizes: {
        xs: "px-2 py-1 text-xs",
        sm: "px-3 py-1.5 text-sm",
        md: "px-4 py-2 text-sm",
        lg: "px-5 py-2.5 text-base",
        xl: "px-6 py-3 text-base"
    },

    // Primary action buttons (main CTAs)
    primary: {
        blue: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm",
        green: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-sm",
        red: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm",
        indigo: "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-sm",
        purple: "bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500 shadow-sm"
    },

    // Secondary action buttons (less prominent)
    secondary: {
        blue: "bg-blue-100 text-blue-700 hover:bg-blue-200 focus:ring-blue-500 border border-blue-200",
        green: "bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500 border border-green-200",
        red: "bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500 border border-red-200",
        indigo: "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 focus:ring-indigo-500 border border-indigo-200",
        gray: "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500 border border-gray-200",
        white: "bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-500 border border-gray-300 shadow-sm"
    },

    // Outline buttons (transparent with border)
    outline: {
        blue: "bg-transparent text-blue-600 hover:bg-blue-50 focus:ring-blue-500 border-2 border-blue-600",
        green: "bg-transparent text-green-600 hover:bg-green-50 focus:ring-green-500 border-2 border-green-600",
        red: "bg-transparent text-red-600 hover:bg-red-50 focus:ring-red-500 border-2 border-red-600",
        gray: "bg-transparent text-gray-600 hover:bg-gray-50 focus:ring-gray-500 border-2 border-gray-600"
    },

    // Ghost buttons (minimal styling)
    ghost: {
        blue: "bg-transparent text-blue-600 hover:bg-blue-50 focus:ring-blue-500",
        gray: "bg-transparent text-gray-600 hover:bg-gray-100 focus:ring-gray-500",
        red: "bg-transparent text-red-600 hover:bg-red-50 focus:ring-red-500"
    },

    // Icon-only buttons
    icon: {
        sm: "p-1.5 rounded-md",
        md: "p-2 rounded-lg",
        lg: "p-2.5 rounded-lg"
    },

    // Special purpose buttons
    special: {
        danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm",
        success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-sm",
        warning: "bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500 shadow-sm",
        info: "bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500 shadow-sm"
    }
};

// Helper function to combine button classes
export const getButtonClasses = (variant = 'primary', color = 'blue', size = 'md', additionalClasses = '') => {
    const baseClasses = buttonStyles.base;
    const sizeClasses = buttonStyles.sizes[size] || buttonStyles.sizes.md;

    let variantClasses = '';

    if (variant === 'primary') {
        variantClasses = buttonStyles.primary[color] || buttonStyles.primary.blue;
    } else if (variant === 'secondary') {
        variantClasses = buttonStyles.secondary[color] || buttonStyles.secondary.blue;
    } else if (variant === 'outline') {
        variantClasses = buttonStyles.outline[color] || buttonStyles.outline.blue;
    } else if (variant === 'ghost') {
        variantClasses = buttonStyles.ghost[color] || buttonStyles.ghost.blue;
    } else if (variant === 'icon') {
        return `${baseClasses} ${buttonStyles.icon[size] || buttonStyles.icon.md} ${additionalClasses}`.trim();
    } else if (buttonStyles.special[variant]) {
        variantClasses = buttonStyles.special[variant];
    }

    return `${baseClasses} ${sizeClasses} ${variantClasses} ${additionalClasses}`.trim();
};

// Common button combinations for specific actions
export const commonButtons = {
    // Action buttons
    save: getButtonClasses('primary', 'blue', 'md'),
    cancel: getButtonClasses('secondary', 'gray', 'md'),
    delete: getButtonClasses('special', 'danger', 'md'),
    add: getButtonClasses('primary', 'green', 'md'),
    edit: getButtonClasses('secondary', 'blue', 'md'),
    refresh: getButtonClasses('secondary', 'white', 'md'),

    // Export/Import buttons
    export: getButtonClasses('secondary', 'green', 'md'),
    import: getButtonClasses('primary', 'green', 'md'),
    pdf: getButtonClasses('secondary', 'red', 'md'),

    // Navigation buttons
    next: getButtonClasses('primary', 'blue', 'md'),
    previous: getButtonClasses('secondary', 'gray', 'md'),

    // AI/Special features
    ai: getButtonClasses('secondary', 'indigo', 'md'),
    help: getButtonClasses('secondary', 'blue', 'md')
};

export default buttonStyles;
